/**
 * THE CASE NAME, SAID PROPERLY — even for one case, even right now.
 *
 * The board asks the model to read its subjects in the background: it answers
 * with what is already known and learns the rest for next time. That is right
 * for two hundred rows and wrong for the ONE name in front of somebody. The
 * dossier picker in France Actes read
 *   "Nowers Citizenship by Descent France — Citizenship · FR - Nationality/Citizenship"
 * because the heuristic was all there was at that instant, and it never got
 * better while the modal stayed open.
 *
 * So: use what the server already attached; otherwise ask for this one subject
 * to be read now (one call, cached for ever), and show the heuristic in the
 * meantime rather than nothing.
 */
import { useQuery } from '@tanstack/react-query';
import { authHeaders } from '@/lib/authHeaders';
import { prettySubject, summarizeSubject, cleanTitle } from '@/lib/subjects';

async function askTidy(subject: string): Promise<{ client: string; service: string }> {
  const res = await fetch(`/api/hubspot?tidy=${encodeURIComponent(subject.slice(0, 300))}`,
    { headers: await authHeaders() });
  if (!res.ok) return { client: '', service: '' };
  return res.json();
}

export interface TidyName {
  /** "Chris Nowers" — never the filing subject. */
  client: string;
  /** "Citizenship by Descent" — at most a few words, never the pipeline id. */
  service: string;
  /** "Chris Nowers — Citizenship by Descent", ready to print. */
  full: string;
  /** True while the model is still reading it (the heuristic is on screen). */
  reading: boolean;
}

export function useTidyName(t: {
  subject?: string | null;
  tidyClient?: string | null;
  tidyService?: string | null;
  pipelineLabel?: string | null;
} | null | undefined): TidyName {
  const subject = (t?.subject || '').trim();
  const have = !!(t?.tidyClient || '').trim();
  const q = useQuery({
    queryKey: ['tidy-name', subject],
    queryFn: () => askTidy(subject),
    enabled: !!subject && !have,
    staleTime: 24 * 60 * 60_000,
    retry: false,
  });
  const guess = summarizeSubject(subject);
  const client = (t?.tidyClient || '').trim() || (q.data?.client || '').trim() || guess.client || prettySubject(subject);
  /* The pipeline label is a LAST resort: "FR - Nationality/Citizenship" is a
     database value, not something a person says. */
  const service = (t?.tidyService || '').trim() || (q.data?.service || '').trim() || guess.service || '';
  return {
    client,
    service,
    full: [client, service].filter(Boolean).join(' — '),
    reading: q.isFetching && !have,
  };
}

/**
 * A DOSSIER'S NAME, WHEN THE STORED ONE IS A FILING STRING.
 *
 * Until today a dossier created from a case took its name from the heuristic,
 * and the heuristic mangles the underscore-and-bracket subjects — so a real
 * dossier in the database is literally called
 *   "Jacques McCormack - Long Stay Visa (Bronze Package) France - (Visitor Visa)".
 * That string is now what the list shows, what search matches, and what every
 * edit prints back. New dossiers no longer get one (the name is read before it
 * is saved), but the ones already stored have to be readable too.
 *
 * So: keep the stored name — somebody may well have typed "Famille Nowers" on
 * purpose — UNLESS it is plainly the case's own subject wearing a hat, in
 * which case say the case's real name instead. The test is deliberately blunt:
 * a stored name that is a slice of the raw subject was not typed by a person.
 */
export function dossierName(stored: string | null | undefined, subject: string | null | undefined, tidyFull: string): string {
  const s = (stored || '').trim();
  if (!s) return tidyFull || '';
  const raw = (subject || '').trim();
  if (!raw || !tidyFull) return displayDossierName(s);
  const fold = (x: string) => x.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  const a = fold(s);
  const b = fold(raw);
  if (!a || !b) return s;
  /* Came out of the subject (a prefix of it, or it of the name) and is long
     enough that it cannot be a coincidence — that is a filing string. */
  if (a.length >= 20 && (b.startsWith(a) || a.startsWith(b) || b.includes(a))) return tidyFull;
  return displayDossierName(s);
}

/**
 * The same repair where fetching the case is not worth it — a list of two
 * hundred dossiers. No network: if the stored name still LOOKS like a filing
 * string (a country token, a bracketed package, underscores), it is read the
 * way every other surface reads a subject. A name a person typed has none of
 * those marks and comes back untouched.
 */
const FILING_MARK = /(_|\((?:visitor|bronze|silver|gold|platinum|citizenship|nationality)[^)]*\)|\b(?:france|spain|espana|españa|portugal|italy|italia)\b)/i;
export function displayDossierName(stored: string | null | undefined): string {
  const s = (stored || '').trim();
  if (!s || s.length < 20 || !FILING_MARK.test(s)) return s;
  const { client } = summarizeSubject(s);
  return (client || cleanTitle(s) || s).trim();
}

/**
 * MANY NAMES AT ONCE — a board of cards, a list of handovers. One request for
 * all the subjects on screen; the server answers what it knows, reads a short
 * remainder now and a long one for next time. Keyed by the subject exactly as
 * given. Cards that already carry their read name are not asked about.
 */
export function useTidyMany(subjects: (string | null | undefined)[]): Record<string, { client: string; service: string }> {
  const list = [...new Set(subjects.map((x) => (x || '').trim().slice(0, 300)).filter(Boolean))].sort();
  const key = list.join('\u0001');
  const q = useQuery({
    queryKey: ['tidy-many', key],
    queryFn: async () => {
      const res = await fetch('/api/hubspot', {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({ action: 'tidyMany', subjects: list }),
      });
      if (!res.ok) return {} as Record<string, { client: string; service: string }>;
      return ((await res.json()) as { names?: Record<string, { client: string; service: string }> }).names || {};
    },
    enabled: list.length > 0,
    staleTime: 24 * 60 * 60_000,
    retry: false,
  });
  return q.data || {};
}
